import {
  HHBox,
  HHCard,
  HHCardContent,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  ArrowForward,
  CloudSync,
  Rule,
  Storage,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useROIRoleBasedRoutes } from '../hooks/use-roi-role-based-routes';

type RouteAccess = ReturnType<typeof useROIRoleBasedRoutes>;

type WorkflowTile = {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: React.ElementType;
  color: string;
  /**
   * Whether the operator may open this workflow. Mirrors the route guard in
   * roi-layout so the dashboard never advertises a tile that redirects.
   */
  isVisible: (access: RouteAccess) => boolean;
};

// Define available workflows
const WORKFLOWS: WorkflowTile[] = [
  {
    id: 'hinge-connect',
    title: 'Hinge Connect',
    description: 'Rules management, user tags, and member data services',
    route: '/roi/hinge-connect',
    icon: Rule,
    color: '#2E7D32', // Green
    // Hinge Connect has no single umbrella permission; any of its sub-areas
    // is enough to land on its own dashboard.
    isVisible: (access): boolean =>
      access.hingeConnectRules ||
      access.hingeConnectTags ||
      access.hingeConnectAssessmentTasks ||
      access.hingeConnectRuleAssessments ||
      access.hingeConnectHealthRecords ||
      access.hingeConnectUserTags,
  },
  {
    id: 'claims-ingestion',
    title: 'Claims Ingestion',
    description: 'Configure data provider file ingestion settings',
    route: '/roi/claims-ingestion',
    icon: Storage,
    color: '#1565C0', // Blue
    isVisible: (access): boolean => access.claimsIngestion,
  },
  {
    id: 'partner-data-gateway',
    title: 'Partner Data Gateway',
    description:
      'File ingestion pipeline monitoring, configuration management, and operational controls',
    route: '/roi/partner-data-gateway',
    icon: CloudSync,
    color: '#7B1FA2', // Purple — distinct from green (Hinge Connect) and blue (Claims Ingestion)
    isVisible: (access): boolean => access.partnerDataGateway,
  },
];

export const ROIDashboard = (): JSX.Element => {
  const navigate = useNavigate();
  const routeAccess = useROIRoleBasedRoutes();

  const handleTileClick = (route: string): void => {
    navigate(route);
  };

  const visibleWorkflows = WORKFLOWS.filter(workflow =>
    workflow.isVisible(routeAccess),
  );

  return (
    <HHBox padding="2rem" bgcolor="#ffffff">
      <HHStack direction="column" spacing={8} height="100%">
        <HHBox sx={{ position: 'relative', marginBottom: '16px' }}>
          <HHStack direction="row" alignItems="center" spacing={3}>
            <HHBox
              sx={{
                backgroundColor:
                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
              }}
            >
              <TrendingUp
                sx={{
                  fontSize: '32px',
                  color: 'white',
                }}
              />
            </HHBox>
            <HHStack direction="column" spacing={0.5}>
              <HHTypography
                hhVariant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '28px', sm: '32px', md: '36px' },
                  lineHeight: 1.2,
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                ROI Dashboard
              </HHTypography>
              <HHTypography
                hhVariant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '18px',
                  fontWeight: 400,
                  marginTop: '4px',
                }}
              >
                Select a workflow to get started
              </HHTypography>
            </HHStack>
          </HHStack>

          {/* Decorative accent line */}
          <HHBox
            sx={{
              position: 'absolute',
              bottom: '-12px',
              left: 0,
              width: '80px',
              height: '4px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '2px',
              opacity: 0.6,
            }}
          />
        </HHBox>

        <HHGrid container spacing={4}>
          {visibleWorkflows.map(workflow => {
            const IconComponent = workflow.icon;
            return (
              <HHGrid item xs={12} sm={6} lg={4} key={workflow.id}>
                <HHCard
                  onClick={(): void => handleTileClick(workflow.route)}
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    minHeight: 200,
                    border: '1px solid #E0E0E0',
                    borderRadius: '12px',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: `${workflow.color}04`,
                    '&:hover': {
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      transform: 'translateY(-4px)',
                      borderColor: workflow.color,
                      backgroundColor: `${workflow.color}08`,
                      '& .workflow-arrow': {
                        transform: 'translateX(4px)',
                        opacity: 1,
                      },
                      '& .workflow-icon': {
                        transform: 'scale(1.1)',
                        color: workflow.color,
                      },
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${workflow.color}`,
                      outlineOffset: '2px',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e): void => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTileClick(workflow.route);
                    }
                  }}
                >
                  <HHCardContent sx={{ padding: '24px', height: '100%' }}>
                    <HHStack
                      direction="column"
                      spacing={3}
                      sx={{ height: '100%' }}
                    >
                      <HHStack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <HHBox
                          sx={{
                            backgroundColor: `${workflow.color}15`,
                            borderRadius: '12px',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconComponent
                            className="workflow-icon"
                            sx={{
                              fontSize: '28px',
                              color: workflow.color,
                              transition:
                                'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </HHBox>
                        <ArrowForward
                          className="workflow-arrow"
                          sx={{
                            fontSize: '20px',
                            color: 'text.secondary',
                            opacity: 0.6,
                            transform: 'translateX(0px)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      </HHStack>

                      <HHStack
                        direction="column"
                        spacing={1}
                        sx={{ flexGrow: 1 }}
                      >
                        <HHTypography
                          hhVariant="h3"
                          sx={{
                            fontWeight: 600,
                            fontSize: '24px',
                            lineHeight: 1.3,
                            color: 'text.primary',
                          }}
                        >
                          {workflow.title}
                        </HHTypography>
                        <HHTypography
                          hhVariant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '16px',
                            lineHeight: 1.5,
                          }}
                        >
                          {workflow.description}
                        </HHTypography>
                      </HHStack>
                    </HHStack>
                  </HHCardContent>
                </HHCard>
              </HHGrid>
            );
          })}
        </HHGrid>
      </HHStack>
    </HHBox>
  );
};
